import { useMemo } from "react";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { Icon } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import defaultStyles from "./PackageCountPickerRow.module.scss";

export type PackageCountPickerRowProps = {
  title: string;
  subtitle?: string;
  count: number;
  minimumCount?: number;
  maximumReached?: boolean;
  setCount: (count: number) => void;
  className?: string;
};

/**
 * A single passenger-category count row that visually matches the flight
 * passenger picker (blue + button, bordered count box) by using the same
 * useFlightStyles hook + CoreFlightsComponent.CountPicker key as the core
 * PassengerCountPicker's internal row component.
 */
export const PackageCountPickerRow = ({
  title,
  subtitle,
  count,
  minimumCount,
  maximumReached = false,
  setCount,
  className,
}: PackageCountPickerRowProps) => {
  const { t } = useI18nContext();
  const styles = useFlightStyles(
    CoreFlightsComponent.CountPicker,
    defaultStyles
  );
  const [, cn] = useModuleBEM(styles, CoreFlightsComponent.CountPicker);

  const minusDisabled = count <= (minimumCount ?? 0);

  const decreaseLabel = useMemo(
    () =>
      t(
        "core-flights.search.passengerSelection.decreaseAmountOfTravelersByCategory",
        { category: title }
      ),
    [t, title]
  );

  const increaseLabel = useMemo(
    () =>
      t(
        "core-flights.search.passengerSelection.increaseAmountOfTravelersByCategory",
        { category: title }
      ),
    [t, title]
  );

  return (
    <div className={clsx(cn("container"), className)}>
      <div className={cn("description-container")}>
        <div className={cn("passenger-type-description")}>
          <div className={cn("passenger-type-title")}>{title}</div>
          {subtitle && (
            <div className={cn("passenger-type-subtitle")}>{subtitle}</div>
          )}
        </div>
      </div>
      <div className={cn("picker-container")}>
        <button
          aria-label={decreaseLabel}
          className={clsx(cn("picker-button-minus"), cn("picker-button"))}
          disabled={minusDisabled}
          onClick={() => {
            if (!minusDisabled) setCount(count - 1);
          }}
        >
          <div
            className={clsx(cn("button-content-wrapper"), {
              [cn("disabled")]: minusDisabled,
            })}
          >
            <Icon iconName={IconNameEnum.minus} />
          </div>
        </button>

        <div className={cn("passenger-count-number-container")}>
          <div className={cn("passenger-count-number")}>
            {count}
            <span
              className={cn("passenger-count-alert")}
              aria-live="polite"
              aria-atomic={true}
            >
              {subtitle ? `${count} ${title} ${subtitle}` : `${count} ${title}`}
            </span>
          </div>
        </div>

        <button
          aria-label={increaseLabel}
          className={cn("passenger-count-picker-button")}
          onClick={() => setCount(count + 1)}
          disabled={maximumReached}
        >
          <div
            className={clsx(cn("button-content-wrapper"), {
              [cn("disabled")]: maximumReached,
            })}
          >
            <Icon iconName={IconNameEnum.plus} />
          </div>
        </button>
      </div>
    </div>
  );
};
